/**
 * Adaptive Assessment™ — baseline scoring utilities.
 * The baseline is a multidimensional nervous-system profile, not a universal score.
 */
import { SECTIONS, ARCHETYPES } from "./assessmentData";
import { loadParticipantIdentity } from "./participantIdentity";
import {
  appendAssessmentLocal,
  beginAssessmentRetakeLocal,
  finishAssessmentRetakeLocal,
  loadActiveAssessmentLocal,
  loadAssessmentHistoryLocal,
  setActiveAssessmentLocal,
} from "./assessmentHistory";
import {
  buildLegacyAnswersFromRobust,
  buildRobustNervousSystemProfile,
  isRobustAssessment,
  robustCompatibilityDimensions,
} from "./robustAssessmentEngine";
import {
  ROBUST_ASSESSMENT_MODEL,
  ROBUST_ASSESSMENT_SCHEMA_VERSION,
} from "./robustAssessmentData";

const avg = (obj) => {
  const vals = Object.values(obj).map(Number).filter(Number.isFinite);
  if (!vals.length) return 3;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const normalize = (answers, sectionId) => {
  const raw = answers[sectionId] || {};
  const out = {};
  const section = SECTIONS.find((item) => item.id === sectionId);
  (section?.items || []).forEach((it) => {
    const numeric = Number(raw[it.key]);
    const value = Number.isFinite(numeric) ? numeric : 3;
    out[it.key] = it.reverse ? 6 - value : value;
  });
  return out;
};

export function computeDimensions(answers) {
  if (isRobustAssessment(answers)) return robustCompatibilityDimensions(answers);
  return {
    state: avg(normalize(answers, "state")),
    inputs: avg(normalize(answers, "inputs")),
    capacity: avg(normalize(answers, "capacity")),
  };
}

const legacyArchetype = (answers) => {
  const { state, inputs, capacity } = computeDimensions(answers);
  const bh = answers.behaviors || [];
  const problemPatterns = [
    "withdrawal",
    "avoidance",
    "scrolling",
    "substance",
    "eating",
    "overwork",
  ];
  const problemCount = bh.filter((b) => problemPatterns.includes(b)).length;

  if (state <= 2.6 && problemCount >= 3) return ARCHETYPES["high-activation"];
  if (capacity <= 2.5 || (state <= 2.8 && inputs <= 2.5)) {
    return ARCHETYPES["reduced-capacity"];
  }
  if (state <= 3.4 && inputs >= 3.2 && capacity >= 3) {
    return ARCHETYPES["recovery-focus"];
  }
  if (
    state >= 3.6 &&
    inputs >= 3.6 &&
    capacity >= 3.6 &&
    problemCount <= 1
  ) {
    return ARCHETYPES["growth-performance"];
  }
  return ARCHETYPES["regulation-building"];
};

export function computeArchetype(answers) {
  if (isRobustAssessment(answers)) {
    const nervousSystemProfile =
      answers.robustProfile || buildRobustNervousSystemProfile(answers);
    return {
      key: nervousSystemProfile.legacyProfileKey,
      title: nervousSystemProfile.headline,
      body: nervousSystemProfile.bottomLine,
      nervousSystemProfile,
      userFacingType: "descriptive-nervous-system-profile",
    };
  }
  return legacyArchetype(answers);
}

/**
 * Baseline focus remains separate from the current nervous-system check-in.
 */
export function computeTodaysFocus(answers) {
  if (isRobustAssessment(answers)) {
    const profile = answers.robustProfile || buildRobustNervousSystemProfile(answers);
    const first = profile.priorities?.[0];
    return {
      lever: first?.title || "nervous-system flexibility",
      text:
        first?.body ||
        "Use the current nervous-system check-in, calendar, capacity, and personal-response history to determine the next relative dose.",
      scores: {
        state: profile.scores.control || 3,
        inputs: profile.scores.resources || 3,
        capacity: profile.scores.capacity || 3,
        behaviors: profile.scores.control || 3,
      },
    };
  }

  const { state, inputs, capacity } = computeDimensions(answers);
  const bh = answers.behaviors || [];
  const problemPatterns = [
    "withdrawal",
    "avoidance",
    "scrolling",
    "substance",
    "eating",
    "overwork",
  ];
  const problemCount = bh.filter((b) => problemPatterns.includes(b)).length;
  const behaviorScore = 5 - Math.min(problemCount, 4);

  const scored = [
    {
      key: "capacity",
      score: capacity,
      focus:
        "Build relative capacity using familiar activity at a nervous-system-state-matched dose that you can control, stop, and recover from.",
    },
    {
      key: "nervous-system-flexibility",
      score: state,
      focus:
        "Build nervous-system flexibility by practising appropriate mobilization, behavioral control, transition, and recovery across different demands.",
    },
    {
      key: "inputs",
      score: inputs,
      focus:
        "Strengthen the inputs that support usable energy and recovery, then let the current nervous-system check-in decide what makes sense today.",
    },
    {
      key: "behaviors",
      score: behaviorScore,
      focus:
        "Notice what happens before the repeated behavior: emotion, physical response, action pressure, environment, and available control provide useful nervous-system information.",
    },
  ].sort((a, b) => a.score - b.score);

  return {
    lever: scored[0].key,
    text: scored[0].focus,
    scores: { state, inputs, capacity, behaviors: behaviorScore },
  };
}

export function saveProfileLocal(sourceAnswers) {
  const identity = loadParticipantIdentity();
  const robust = isRobustAssessment(sourceAnswers);
  const nervousSystemProfile = robust
    ? buildRobustNervousSystemProfile(sourceAnswers)
    : null;
  const answers = robust
    ? buildLegacyAnswersFromRobust({
        ...sourceAnswers,
        robustProfile: nervousSystemProfile,
      })
    : sourceAnswers;
  const profile = computeArchetype(answers);
  const completedAt = Date.now();
  const schemaVersion = robust ? ROBUST_ASSESSMENT_SCHEMA_VERSION : 4;
  const result = appendAssessmentLocal({
    id: `assessment-${completedAt}`,
    schemaVersion,
    assessmentModel: robust ? ROBUST_ASSESSMENT_MODEL : null,
    completedAt,
    updatedAt: new Date(completedAt).toISOString(),
    identity,
    answers,
    profileKey: profile.key,
    profileTitle: profile.title,
    dimensions: computeDimensions(answers),
    baselineVector: nervousSystemProfile?.vector || null,
    nervousSystemProfile,
    appVersion: "apos-robust-assessment-v5",
  });
  finishAssessmentRetakeLocal();
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("adaptive-profile-saved", {
        detail: {
          assessmentId: result.record.id,
          completedAt: result.record.completedAt,
          participant: result.record.identity,
          assessmentCount: result.history.length,
          schemaVersion,
          assessmentModel: result.record.assessmentModel,
        },
      }),
    );
    window.dispatchEvent(new CustomEvent("apos-local-data-changed"));
  }
  return result.record;
}

export function loadProfileLocal() {
  const record = loadActiveAssessmentLocal();
  if (!record?.answers) return null;
  return {
    v: Number(record.schemaVersion) || 4,
    id: record.id,
    a: record.answers,
    i: record.identity || loadParticipantIdentity(),
    t: record.completedAt,
    profileKey: record.profileKey,
    profileTitle: record.profileTitle,
    dimensions: record.dimensions,
    assessmentModel: record.assessmentModel,
    baselineVector: record.baselineVector,
    nervousSystemProfile:
      record.nervousSystemProfile || record.answers.robustProfile || null,
  };
}

export function loadProfilesLocal() {
  return loadAssessmentHistoryLocal();
}

export function activateProfileLocal(id) {
  return setActiveAssessmentLocal(id);
}

export function clearProfileLocal() {
  beginAssessmentRetakeLocal();
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("adaptive-profile-cleared", {
        detail: { preserveHistory: true },
      }),
    );
  }
}
